import { get, chain, includes, isEmpty, sumBy, forEach, keyBy, some, capitalize, omit, map, find, keys, mapValues, isArray, pick, filter } from "lodash"
import { getAggregatedFactorsList, aggregateFactors } from "helpers/factorClassification"
import { createSelector } from "reselect"
import { stripHtml } from "helpers/string"

export function getFactorLabel(factors, id) {
  const factor = get(factors, id, { label: id })
  const rawLabel = factor.overviewLabel || factor.label

  return stripHtml(rawLabel)
}

export function getFactorShortLabel(factors, id, defaultLabel) {
  const factor = get(factors, id)
  if (!factor) {
    return defaultLabel ?? id
  }
  return factor.alias || factor.label || id
}

export function getFactorCategory(factors, id) {
  return get(factors, [id, "category"])
}

export function getIsAggregatedFactor(factors, id) {
  return get(factors, [id, "isAggregated"])
}

export function getFactorDisabled(category) {
  return includes(["esg", "beta"], category)
}

export function getFactorName(factorId, contentSetId, factorsMetadata, contentSets) {
  if (contentSetId) {
    return chain(contentSets)
      .find(({ id }) => id === contentSetId)
      .get("factors")
      .find(({ id }) => id === factorId)
      .get("name", factorId)
      .value()
  } else {
    return get(factorsMetadata, [factorId, "label"], factorId)
  }
}

export function getContentSetLabel(factorsMetadata, id, contentSetId) {
  const factors = get(factorsMetadata, contentSetId)
  if (factors) {
    return chain(factors).find(({ id: factorId }) => id === factorId).get("name", id)
  }
  return id
}

export function computeFactorCategories(factors, selectedViews) {
  const factorCategories = chain(factors)
    .groupBy("category")
    .mapValues(factors => sumBy(filter(factors, factor => !factor.customView), "value"))
    .value()

  forEach(selectedViews, selectedView => {
    factorCategories[selectedView.id] = chain(factors)
      .find(factor => factor.id === selectedView.id)
      .get("value")
      .value()
  })

  return factorCategories
}

export function mergeCategories(categories, selectedViews) {
  if (isEmpty(selectedViews)) {
    return categories
  }

  return [
    ...categories,
    ...selectedViews,
  ]
}

export function computeAvailableSelectViewFactors(factors, factorsClassification, selectedViews, nonZeroKeys = {}) {
  const aggregatedNonZeroKeys = map(aggregateFactors(factorsClassification, nonZeroKeys), ({ id }) => id)
  return chain(factors)
    .thru(factors => nonZerosKeysFilter(factors, aggregatedNonZeroKeys))
    .filter(factor => factor.isAggregated && !some(selectedViews, selectedView => selectedView.id === factor.id))
    .map(item => ({
      ...item,
      type: "factor",
      name: item.name || item.label,
      info: capitalize(item.category),
    }))
    .value()
}

export function computeSelectedViews(rawSelectedViews, factors, mustAggregateFactors) {
  if (isEmpty(rawSelectedViews) || !mustAggregateFactors) {
    return null
  }

  return chain(rawSelectedViews)
    .map(selectedView => {
      const selectedFactorView = factors[selectedView]
      if (!selectedFactorView) {
        return null
      }

      return {
        ...omit(selectedFactorView, "category"),
        category: selectedFactorView.id,
        isSelectedView: true,
        color: "#FFCF0D",
      }
    })
    .compact()
    .value()
}

export function computeSelectedViewsFactors(selectedViews, factors, factorsClassification) {
  if (isEmpty(factorsClassification) || isEmpty(selectedViews)) {
    return []
  }

  return chain(selectedViews)
    .map(selectedView => {
      const aggregatedFactors = getAggregatedFactorsList(factorsClassification, factors, selectedView.id)
      return map(aggregatedFactors, factor => ({ id: factor, selectedView: selectedView.id }))
    })
    .flatten()
    .value()
}

export function makeNonZeroKeysSelectorFromData({ getter = (v) => v, isAboveZeroTreshold = (v) => v, dataSelector, favoriteSelector, factorValueField = "value" }) {
  return createSelector(
    [dataSelector, favoriteSelector],
    (data, favorites = {}) => {
      const itemFactorsGetter = (item) => chain(item)
        .thru(getter)
        .omit(keys(favorites))
        .reduce((acc, item) => ({ ...acc, [item.id]: get(item, factorValueField) }), {})
        .value()

      return chain(data)
        .reduce((items, item) => {
          return mapValues(itemFactorsGetter(item), (value, key) => get(items, key) || isAboveZeroTreshold(value))
        }, {})
        .merge(favorites)
        // The aggregateFactors() function expects an array of factor objects { id, ... }
        .map((isNotZero, key) => isNotZero && { id: key })
        .compact()
        .value()
    })
}

export function nonZerosKeysFilter(data, keys) {
  if (isArray(data)) {
    return chain(data)
      .keyBy("id")
      .pick(keys)
      .map()
      .value()
  }
  return pick(data, keys)
}

export function computeFactorsMetadata(rawFactors, factorsClassification, selectedViewsFactors, { isStyleFactorsAdditive, styleAggregation, aggregationFieldName, shouldAggregateFactors = true } = {}) {
  if (isEmpty(factorsClassification)) {
    return rawFactors
  }

  let factors = rawFactors
  if (shouldAggregateFactors) {
    factors = aggregateFactors(factorsClassification, rawFactors, aggregationFieldName, { isStyleFactorsAdditive, styleAggregation })
    factors = keyBy(factors, "id")
  }

  if (isEmpty(selectedViewsFactors)) {
    return factors
  }

  return {
    ...factors,
    ...chain(rawFactors)
      .pick(map(selectedViewsFactors, "id"))
      .mapValues((factor, id) => {
        const selectedView = find(selectedViewsFactors, item => item.id === id)
        // When factors are not aggregated, we need to keep the same category,
        // but add another property to the factor for it to display in the custom view
        if (!shouldAggregateFactors || !isStyleFactorsAdditive) {
          return { ...factor, selectedView: selectedView.selectedView }
        }

        return { ...factor, category: selectedView.selectedView }
      })
      .value(),
  }
}
